import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { examApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ExamInsightPanel from '../components/ExamInsightPanel';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import './ExamResultsPage.css';
import { formatDate } from '../utils/format';
import { chartTheme } from '../styles/chartTheme';
import { getApiErrorCode, getApiErrorMessage } from '../utils/examStart';
import LinkifiedText from '../components/LinkifiedText';

interface ExamResultsPageProps {
  sessionId: string;
  onBack: () => void;
  userId?: string;
  /** 틀린 문제 다시 풀기로 새 세션을 만들었을 때 시험 화면으로 이동 */
  onStartExam?: (sessionId: string) => void;
}

interface DomainResult {
  domain: string;
  name: string;
  weight: number;
  total: number;
  correct: number;
  percentage: number | null;
}

interface ResultQuestion {
  question_id: string;
  question_order: number;
  question_text: string;
  options: Array<{ option_number: number; option_text: string }>;
  user_answers: number[];
  correct_answers: number[];
  is_correct: boolean;
  answered_at?: string;
  explanation: string;
  reasoning: string;
  /** 보기별 해설 (화면 번호 기준) */
  option_notes?: Array<{ option_number: number; is_correct: boolean; note: string }>;
}

interface ExamResults {
  sessionId: string;
  score: number;
  percentage: number;
  totalQuestions: number;
  correctAnswers: number;
  answeredQuestions: number;
  timeTakenMinutes: number;
  passed: boolean;
  startedAt: string;
  completedAt: string;
  examType?: string;
  mode?: 'practice' | 'exam';
  kind?: 'incorrect-review' | 'retry-incorrect';
  passingScore?: number;
  domainBreakdown?: DomainResult[];
  questions: ResultQuestion[];
}

// 틀린 문제가 있으면 오답부터 보여 줘서 바로 복습할 수 있게 합니다.
const pickInitialFilter = (data?: ExamResults): 'all' | 'incorrect' =>
  data?.questions?.some((question) => !question.is_correct) ? 'incorrect' : 'all';

const ExamResultsPage: React.FC<ExamResultsPageProps> = ({ sessionId, onBack, onStartExam }) => {
  const [results, setResults] = useState<ExamResults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const handleRetryIncorrect = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const response = await examApi.retryIncorrect(sessionId) as { success: boolean; data?: { sessionId: string } };
      if (response.success && response.data?.sessionId) {
        onStartExam?.(response.data.sessionId);
      } else {
        setRetryError('오답 연습을 시작하지 못했습니다.');
      }
    } catch (retryFailure) {
      setRetryError(
        getApiErrorCode(retryFailure) === 'ACTIVE_SESSION_EXISTS'
          ? '진행 중인 시험이 있습니다. 대시보드에서 이어서 풀거나 포기한 뒤 다시 시도해 주세요.'
          : getApiErrorMessage(retryFailure) || '오답 연습을 시작하지 못했습니다.',
      );
    } finally {
      setRetrying(false);
    }
  };

  const { user } = useAuth();

  useEffect(() => {
    const loadResults = async () => {
      try {
        if (user?.is_admin) {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/exam/${sessionId}/detailed-results`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          });
          const data = await response.json();
          if (data.success) {
            setResults(data.data as ExamResults);
            setFilter(pickInitialFilter(data.data as ExamResults));
          } else {
            setError('결과를 불러올 수 없습니다.');
          }
        } else {
          const response = await examApi.getResults(sessionId);
          if (response.success) {
            setResults(response.data as ExamResults);
            setFilter(pickInitialFilter(response.data as ExamResults));
          } else {
            setError('결과를 불러올 수 없습니다.');
          }
        }
      } catch {
        setError('결과를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
  }, [sessionId, user]);

  const toggleQuestion = (questionOrder: number) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionOrder)) {
        next.delete(questionOrder);
      } else {
        next.add(questionOrder);
      }
      return next;
    });
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  };

  const filteredQuestions = useMemo(() => {
    if (!results) return [];
    return results.questions.filter((q) => {
      if (filter === 'correct') return q.is_correct;
      if (filter === 'incorrect') return !q.is_correct;
      return true;
    });
  }, [results, filter]);

  const expandAll = () => {
    setExpandedQuestions(new Set(filteredQuestions.map((q) => q.question_order)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const formatAnswerList = (answers: number[]) => {
    if (!answers || answers.length === 0) return '-';
    return answers.join(', ');
  };

  const gaugeOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true }
    },
    plotOptions: {
      radialBar: {
        startAngle: -140,
        endAngle: 140,
        hollow: {
          size: '68%'
        },
        track: {
          background: chartTheme.track,
          strokeWidth: '100%',
          margin: 0
        },
        dataLabels: {
          name: {
            show: true,
            offsetY: 24,
            color: chartTheme.body,
            fontSize: '13px',
            fontWeight: 700
          },
          value: {
            offsetY: -10,
            color: chartTheme.ink,
            fontSize: '34px',
            fontWeight: 800,
            formatter: (val) => `${Math.round(val)}%`
          }
        }
      }
    },
    labels: [results?.passed ? '합격' : '불합격'],
    colors: [results?.passed ? chartTheme.success : chartTheme.danger],
    fill: { type: 'solid' },
    stroke: {
      lineCap: 'round'
    }
  }), [results?.passed]);

  if (isLoading) {
    return (
      <div className="results-v3 results-v3--loading">
        <div className="results-v3__spinner"></div>
        <p>시험 결과를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="results-v3">
        <Card padding="lg" shadow="md">
          <div className="results-v3__error-box">
            <h3>결과 조회 실패</h3>
            <p>{error || '결과를 찾을 수 없습니다.'}</p>
            <Button variant="primary" onClick={onBack}>돌아가기</Button>
          </div>
        </Card>
      </div>
    );
  }

  const correctCount = results.questions.filter((q) => q.is_correct).length;
  const incorrectCount = results.questions.length - correctCount;
  const passingScore = results.passingScore && results.passingScore <= 100 ? results.passingScore : 72;
  const domainResults = (results.domainBreakdown || []).filter((item) => item.percentage !== null);
  // 2문항 이상 나온 영역 중 정답률이 가장 낮은 곳
  const weakestDomain = [...domainResults]
    .filter((item) => item.total >= 2)
    .sort((left, right) => (left.percentage ?? 0) - (right.percentage ?? 0))[0];
  const canRetry = incorrectCount > 0 && Boolean(onStartExam);
  const clampedScore = Math.max(0, Math.min(100, results.percentage));

  return (
    <div className="results-v3">
      <section className="results-v3__hero">
        <div className="results-v3__hero-top">
          <Button variant="outline-secondary" onClick={onBack}>← 대시보드</Button>
          <span className="results-v3__hero-tags">
            {results.kind === 'incorrect-review' && <span className="results-v3__mode">오답 모의고사</span>}
            {results.mode === 'practice' && <span className="results-v3__mode">{results.kind === 'retry-incorrect' ? '오답 다시 풀기' : '연습 모드'}</span>}
            <span className={`results-v3__status ${results.passed ? 'passed' : 'failed'}`}>
              {results.passed ? 'PASS' : 'FAIL'}
            </span>
          </span>
        </div>
        <h1>시험 결과 리포트</h1>
        <p>핵심 지표와 문항별 해설을 빠르게 확인하고 다음 학습 우선순위를 정리하세요.</p>
      </section>

      <div className="results-v3__layout">
        <aside className="results-v3__sidebar">
          <Card className="results-v3__score-card" padding="lg" shadow="md">
            <div className="results-v3__score-label">총점</div>
            <div className="results-v3__gauge-wrap">
              <div className="results-v3__gauge-chart">
                <ReactApexChart
                  type="radialBar"
                  options={gaugeOptions}
                  series={[clampedScore]}
                  height={176}
                />
              </div>
              <div className="results-v3__pass-line">
                <span>합격선 {passingScore}%</span>
              </div>
            </div>
            <div className="results-v3__score-meta">
              <span>{results.correctAnswers}/{results.totalQuestions} 정답</span>
              <span>{formatTime(results.timeTakenMinutes)}</span>
            </div>
          </Card>

          <Card className="results-v3__mini-grid" padding="md" shadow="sm">
            <div className="results-v3__mini-item">
              <span>정답</span>
              <strong>{correctCount}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>오답</span>
              <strong>{incorrectCount}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>응답</span>
              <strong>{results.answeredQuestions}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>제출</span>
              <strong>{formatDate(results.completedAt)}</strong>
            </div>
          </Card>

          {(canRetry || domainResults.length > 0) && (
            <Card className="results-v3__next" padding="md" shadow="sm" title="다음 학습">
              {weakestDomain && (
                <p className="results-v3__weakest">
                  최우선 보강 영역: <strong>{weakestDomain.name}</strong>
                  <span className="tabular">
                    {' '}({weakestDomain.correct}/{weakestDomain.total}, {weakestDomain.percentage}%
                    {(weakestDomain.percentage ?? 0) < passingScore ? ` · 합격선 대비 ${(weakestDomain.percentage ?? 0) - passingScore}%p` : ''})
                  </span>
                </p>
              )}
              {domainResults.length > 0 && (
                <ul className="results-v3__domains" aria-label="출제 영역별 정답률">
                  {domainResults.map((item) => (
                    <li key={item.domain} className={item.domain === weakestDomain?.domain ? 'is-weakest' : undefined}>
                      <div className="results-v3__domain-head">
                        <span>{item.name}</span>
                        <span className="tabular">{item.correct}/{item.total} · {item.percentage}%</span>
                      </div>
                      <div className="results-v3__domain-bar" aria-hidden="true">
                        <span
                          className={(item.percentage ?? 0) >= passingScore ? 'is-pass' : 'is-fail'}
                          style={{ width: `${item.percentage ?? 0}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {canRetry && (
                <div className="results-v3__retry">
                  <Button variant="primary" fullWidth onClick={handleRetryIncorrect} loading={retrying}>
                    틀린 문제 {incorrectCount}개 다시 풀기
                  </Button>
                  <p>연습 모드로 시작하고, 보기 순서는 매번 섞입니다.</p>
                  {retryError && <p className="results-v3__retry-error" role="alert">{retryError}</p>}
                </div>
              )}
            </Card>
          )}

        </aside>

        <main className="results-v3__content">
          {/* 학습 진단 리포트: 실전 모드는 제출 시 자동, 연습 모드는 버튼으로. 대상이 아니면 안내만 */}
          {results.kind !== 'retry-incorrect' && <ExamInsightPanel sessionId={sessionId} practice={results.mode === 'practice'} />}
          <Card padding="lg" shadow="md" className="results-v3__questions-wrap">
            <div className="results-v3__questions-head">
              <h2>문항별 상세 결과</h2>
              <div className="results-v3__head-actions">
                <button type="button" onClick={expandAll}>전체 펼치기</button>
                <button type="button" onClick={collapseAll}>전체 닫기</button>
              </div>
            </div>

            <div className="results-v3__filters">
              <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
                전체 {results.questions.length}
              </button>
              <button className={filter === 'correct' ? 'active' : ''} onClick={() => setFilter('correct')}>
                정답 {correctCount}
              </button>
              <button className={filter === 'incorrect' ? 'active' : ''} onClick={() => setFilter('incorrect')}>
                오답 {incorrectCount}
              </button>
            </div>

            <div className="results-v3__quick-table-wrap">
              <table className="results-v3__quick-table">
                <thead>
                  <tr>
                    <th>문항</th>
                    <th>내 답</th>
                    <th>정답</th>
                    <th>결과</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map((question) => (
                    <tr key={`quick-${question.question_order}`}>
                      <td>{question.question_order}</td>
                      <td>{formatAnswerList(question.user_answers)}</td>
                      <td>{formatAnswerList(question.correct_answers)}</td>
                      <td>
                        <span className={`results-v3__badge ${question.is_correct ? 'correct' : 'incorrect'}`}>
                          {question.is_correct ? '정답' : '오답'}
                        </span>
                      </td>
                      <td className="results-v3__quick-action">
                        <button type="button" onClick={() => toggleQuestion(question.question_order)}>
                          {expandedQuestions.has(question.question_order) ? '닫기' : '펼치기'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="results-v3__question-list">
              {filteredQuestions.map((question) => {
                const expanded = expandedQuestions.has(question.question_order);
                return (
                  <article
                    key={question.question_order}
                    className="results-v3__question-item"
                    data-question-order={question.question_order}
                  >
                    <button
                      className="results-v3__question-header"
                      onClick={() => toggleQuestion(question.question_order)}
                    >
                      <div className="results-v3__question-head-left">
                        <span className="results-v3__q-number">문제 {question.question_order}</span>
                        <span className={`results-v3__badge ${question.is_correct ? 'correct' : 'incorrect'}`}>
                          {question.is_correct ? '정답' : '오답'}
                        </span>
                      </div>
                      <span className="results-v3__expand">{expanded ? '접기 −' : '펼치기 +'}</span>
                    </button>

                    {expanded && (
                      <div className="results-v3__question-body">
                        <p className="results-v3__question-text">{question.question_text}</p>

                        <div className="results-v3__options">
                          {question.options.map((option) => {
                            const isUserAnswer = question.user_answers.includes(option.option_number);
                            const isCorrectAnswer = question.correct_answers.includes(option.option_number);

                            let optionClass = 'results-v3__option';
                            if (isCorrectAnswer) optionClass += ' correct-answer';
                            if (isUserAnswer && !isCorrectAnswer) optionClass += ' wrong-answer';
                            if (isUserAnswer && isCorrectAnswer) optionClass += ' correct-user-answer';

                            const note = question.option_notes?.find((item) => item.option_number === option.option_number)?.note;
                            return (
                              <div key={option.option_number} className={optionClass}>
                                <span className="results-v3__option-no">{option.option_number}</span>
                                <span className="results-v3__option-body">
                                  <span className="results-v3__option-text">{option.option_text}</span>
                                  {note && <span className="results-v3__option-note">{note}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="results-v3__explain">
                          <h4>해설</h4>
                          <p><LinkifiedText text={question.explanation || ''} /></p>
                          {/* 보기별 해설은 위 각 보기 아래에 붙어 있습니다. 나눌 수 없는 형식일 때만 원문을 보여 줍니다. */}
                          {!(question.option_notes && question.option_notes.length > 0) && question.reasoning && (
                            <>
                              <h4>보기별 근거</h4>
                              <p>{question.reasoning}</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default ExamResultsPage;
